/**
 * Всё время с сервера приходит в UTC. Показываем его в часовом поясе школы,
 * а не браузера: ученик в отпуске не должен видеть занятие сдвинутым.
 */

/**
 * Формат времени пришпилен к 24 часам: hourCycle 'h23' не даёт браузеру
 * подставить am/pm по локали системы и печатает полночь как 00:00, а не 24:00.
 */
const TIME_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  hourCycle: 'h23',
} as const satisfies Intl.DateTimeFormatOptions;

export function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone, ...TIME_OPTIONS }).format(new Date(iso));
}

export function formatDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'short',
    ...TIME_OPTIONS,
  }).format(new Date(iso));
}

export function formatWeekday(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone, weekday: 'short' }).format(new Date(iso));
}

export function formatDayNumber(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone, day: 'numeric' }).format(new Date(iso));
}

export function formatMonth(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone, month: 'long', year: 'numeric' }).format(
    new Date(iso),
  );
}

/** «2026-08-03» в заданном поясе — ключ для группировки по дням. */
export function dayKeyOf(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
  return parts;
}

/**
 * 0 — сегодня, 1 — завтра, иначе null. Сравнение идёт по ключу дня в поясе
 * школы: у подписей к дню три разных формы, и все они должны считать «сегодня»
 * одинаково, иначе кнопка дня и заголовок под ней разойдутся на границе суток.
 */
function relativeDay(iso: string, timeZone: string): 0 | 1 | null {
  const target = dayKeyOf(iso, timeZone);
  const now = new Date();

  if (target === dayKeyOf(now.toISOString(), timeZone)) return 0;
  if (target === dayKeyOf(new Date(now.getTime() + 86_400_000).toISOString(), timeZone)) return 1;

  return null;
}

export function isToday(iso: string, timeZone: string): boolean {
  return relativeDay(iso, timeZone) === 0;
}

/** «сегодня», «завтра» или «пн» — верхняя строка на кнопке дня. */
export function shortDayLabel(iso: string, timeZone: string): string {
  const relative = relativeDay(iso, timeZone);
  if (relative === 0) return 'сегодня';
  if (relative === 1) return 'завтра';
  return formatWeekday(iso, timeZone);
}

/** «сегодня», «завтра» или дата — так шапка дня читается быстрее. */
export function humanDayLabel(iso: string, timeZone: string): string {
  const relative = relativeDay(iso, timeZone);
  if (relative === 0) return 'Сегодня';
  if (relative === 1) return 'Завтра';

  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

/**
 * «Сегодня, 31 августа» — заголовок выбранного дня. Относительное слово
 * читается быстрее, но без числа непонятно, куда именно ученик записывается,
 * когда список дней уходит в следующий месяц.
 */
export function fullDayLabel(iso: string, timeZone: string): string {
  const relative = relativeDay(iso, timeZone);
  if (relative === null) return humanDayLabel(iso, timeZone);
  return `${humanDayLabel(iso, timeZone)}, ${formatDate(iso, timeZone)}`;
}

/** Склонение по числу: 1 окно, 2 окна, 5 окон. */
export function pluralize(count: number, one: string, few: string, many: string): string {
  const lastTwo = Math.abs(Math.trunc(count)) % 100;
  const last = lastTwo % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

/** Группировка списка по дню с сохранением порядка. */
export function groupByDay<T>(
  items: T[],
  getIso: (item: T) => string,
  timeZone: string,
): Array<{ dayKey: string; iso: string; items: T[] }> {
  const groups = new Map<string, { dayKey: string; iso: string; items: T[] }>();

  for (const item of items) {
    const iso = getIso(item);
    const key = dayKeyOf(iso, timeZone);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { dayKey: key, iso, items: [item] });
    }
  }

  return [...groups.values()];
}

/** Локальная дата в формате input[type=date] с учётом пояса школы. */
export function toDateInputValue(date: Date, timeZone: string): string {
  return dayKeyOf(date.toISOString(), timeZone);
}
